import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../ToastSystem';
import {
  buildEventImageFileUrl,
  buildEventImagePreviewUrl,
  createEventImageBatch,
  createEventImageFolder,
  deleteEventImages,
  getEventImageSummary,
  getEventImageTree,
  moveEventImages,
} from '../../../api/eventDetails';
import {
  uploadEventImageFile,
} from '../../../api/storage';
import EventImageGalleryView from './EventImageGalleryView';
import {
  normalizeImageForUpload,
  normalizeImagesForUpload,
} from '../../../utils/imageUploadCompatibility';
import {
  ACCEPTED_IMAGE_TYPES,
  createDragPreview,
  getImageDimensions,
  hasExternalFiles,
  isDroppableTransfer,
  loadStoredWatermarkSettings,
  MAX_BATCH_FILES,
  MAX_IMAGE_BYTES,
  REGISTRATION_BATCH_FILES,
  renderProcessedImage,
  saveStoredWatermarkSettings,
  UPLOAD_STEPS,
} from './EventImageGallery.helpers';
import '../../../css/common/events/EventImageGallery.css';

function EventImageGallery({
  eventUuid,
  eventName = '',
  eventDateLabel = '',
  canView = true,
  canUpload = false,
  canDownload = false,
  showTitle = true,
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const fileInputRef = useRef(null);
  const customLogoInputRef = useRef(null);
  const processedPreviewsRef = useRef([]);
  const springFolderTimerRef = useRef(null);
  const springFolderTargetRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [path, setPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImageId, setActiveImageId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState('watermark');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [processedPreviews, setProcessedPreviews] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const uploadSessionRef = useRef(null);
  const treeLoadRequestRef = useRef(0);
  const [sampleOrientation, setSampleOrientation] = useState('landscape');
  const [sampleTone, setSampleTone] = useState('dark');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dragPayload, setDragPayload] = useState(null);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState(null);
  const [settings, setSettings] = useState(() => loadStoredWatermarkSettings());

  const previewImages = images;
  const usage = summary?.usage || {};
  const limits = summary?.limits || {};
  const selectedCount = selectedIds.size;
  const uploadStepIndex = Math.max(0, UPLOAD_STEPS.findIndex((step) => step.id === uploadStep));
  const activeImageIndex = Math.max(0, previewImages.findIndex((image) => image.id === activeImageId));
  const activeImage = previewImages[activeImageIndex] || previewImages[0] || null;

  const loadData = useCallback(async (nextFolderId = null) => {
    if (!eventUuid || !canView) return;
    const requestId = treeLoadRequestRef.current + 1;
    treeLoadRequestRef.current = requestId;
    setLoading(true);
    setFolders([]);
    setImages([]);
    setPath([]);
    setFolderId(nextFolderId ?? null);
    setSelectedIds(new Set());
    try {
      const summaryPromise = getEventImageSummary(eventUuid).then(
        (value) => ({ value, error: null }),
        (error) => ({ value: null, error })
      );
      let offset = 0;
      let hasMore = true;
      let firstPage = true;
      while (hasMore) {
        const page = await getEventImageTree(eventUuid, nextFolderId, { limit: 100, offset });
        if (treeLoadRequestRef.current !== requestId) return;
        const pageFolders = Array.isArray(page.folders) ? page.folders : [];
        const pageImages = Array.isArray(page.images) ? page.images : [];
        const mergeUnique = (current, incoming) => Array.from(
          new Map([...current, ...incoming].map((item) => [String(item.id), item])).values()
        );
        if (firstPage) {
          setFolderId(page.current_folder_id ?? nextFolderId ?? null);
          setPath(Array.isArray(page.path) ? page.path : []);
          setFolders(pageFolders);
          setImages(pageImages);
          firstPage = false;
        } else {
          setFolders((current) => mergeUnique(current, pageFolders));
          setImages((current) => mergeUnique(current, pageImages));
        }
        hasMore = Boolean(page.has_more ?? page.hasMore);
        offset += 100;
      }
      const summaryResult = await summaryPromise;
      if (treeLoadRequestRef.current !== requestId) return;
      if (summaryResult.error) throw summaryResult.error;
      setSummary(summaryResult.value);
    } catch (error) {
      if (treeLoadRequestRef.current !== requestId) return;
      console.error('Event image load failed:', error);
      toastError(error?.message || 'イベントアルバムの取得に失敗しました');
    } finally {
      if (treeLoadRequestRef.current === requestId) setLoading(false);
    }
  }, [canView, eventUuid, toastError]);

  useEffect(() => {
    loadData(null);
  }, [loadData]);

  useEffect(() => () => {
    treeLoadRequestRef.current += 1;
  }, []);

  useEffect(() => {
    saveStoredWatermarkSettings(settings);
  }, [settings]);

  const resetUploadDraft = useCallback(() => {
    setUploadStep('watermark');
    setPendingFiles([]);
    setUploadTargetFolderId(null);
    setProcessedPreviews((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.url));
      return [];
    });
    setPreviewBusy(false);
    setUploadProgress(null);
    uploadSessionRef.current = null;
  }, []);

  const openUploadWizard = useCallback((targetFolderId = folderId) => {
    resetUploadDraft();
    setUploadTargetFolderId(targetFolderId ?? null);
    setUploadOpen(true);
  }, [folderId, resetUploadDraft]);

  useEffect(() => {
    if (!uploadOpen || uploadStep !== 'confirm' || !pendingFiles.length) {
      setProcessedPreviews((current) => {
        current.forEach((item) => URL.revokeObjectURL(item.url));
        return [];
      });
      return undefined;
    }

    let active = true;
    setPreviewBusy(true);
    setProcessedPreviews((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.url));
      return [];
    });

    const buildPreviews = async () => {
      try {
        const previews = [];
        const targets = pendingFiles.slice(0, 1);
        for (const file of targets) {
          const processed = await renderProcessedImage(file, settings, { eventName, eventDate: eventDateLabel });
          const url = URL.createObjectURL(processed.file);
          previews.push({
            name: file.name,
            url,
            fileSize: processed.file.size,
            originalSize: processed.originalSize,
            resized: processed.resized,
          });
        }
        if (!active) {
          previews.forEach((item) => URL.revokeObjectURL(item.url));
          return;
        }
        setProcessedPreviews(previews);
      } catch (error) {
        console.error('Event image preview failed:', error);
        if (active) toastError(error?.message || '加工後プレビューの生成に失敗しました');
      } finally {
        if (active) setPreviewBusy(false);
      }
    };

    buildPreviews();
    return () => {
      active = false;
    };
  }, [eventDateLabel, eventName, pendingFiles, settings, toastError, uploadOpen, uploadStep]);

  useEffect(() => {
    processedPreviewsRef.current = processedPreviews;
  }, [processedPreviews]);

  useEffect(() => () => {
    processedPreviewsRef.current.forEach((item) => URL.revokeObjectURL(item.url));
  }, []);

  const openFolder = useCallback((nextFolderId) => {
    setFolderId(nextFolderId ?? null);
    loadData(nextFolderId ?? null);
  }, [loadData]);

  const clearSpringFolder = useCallback(() => {
    if (springFolderTimerRef.current) {
      window.clearTimeout(springFolderTimerRef.current);
      springFolderTimerRef.current = null;
    }
    springFolderTargetRef.current = null;
  }, []);

  useEffect(() => () => clearSpringFolder(), [clearSpringFolder]);

  const scheduleSpringFolder = useCallback((nextFolderId, targetKey) => {
    if (springFolderTargetRef.current === targetKey) return;
    clearSpringFolder();
    springFolderTargetRef.current = targetKey;
    springFolderTimerRef.current = window.setTimeout(() => {
      springFolderTimerRef.current = null;
      springFolderTargetRef.current = null;
      setDragOver(false);
      setDropTargetId(null);
      openFolder(nextFolderId);
    }, 650);
  }, [clearSpringFolder, openFolder]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openImagePreview = useCallback((id) => {
    setActiveImageId(id);
    setGalleryOpen(true);
  }, []);

  const moveActiveImage = useCallback((direction) => {
    setActiveImageId((currentId) => {
      if (!previewImages.length) return null;
      const currentIndex = Math.max(0, previewImages.findIndex((image) => image.id === currentId));
      const nextIndex = (currentIndex + direction + previewImages.length) % previewImages.length;
      return previewImages[nextIndex].id;
    });
  }, [previewImages]);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        moveActiveImage(-1);
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        moveActiveImage(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, moveActiveImage]);

  const moveDraggedImages = useCallback(async (targetFolderId) => {
    const imageIds = dragPayload?.type === 'images' ? dragPayload.imageIds : [];
    if (!imageIds?.length || busy) return;
    const normalizedTarget = targetFolderId ?? null;
    const sourceImages = images.filter((image) => imageIds.includes(image.id));
    if (sourceImages.length && sourceImages.every((image) => (image.folder_id ?? null) === normalizedTarget)) {
      setDragPayload(null);
      setDropTargetId(null);
      return;
    }
    setBusy(true);
    try {
      await moveEventImages(eventUuid, imageIds, normalizedTarget);
      setSelectedIds(new Set());
      await loadData(folderId);
      toastSuccess(imageIds.length === 1 ? '画像を移動しました' : `${imageIds.length}枚の画像を移動しました`);
    } catch (error) {
      toastError(error?.message || 'イベントアルバム画像の移動に失敗しました');
    } finally {
      setBusy(false);
      setDragPayload(null);
      setDropTargetId(null);
      setDragOver(false);
    }
  }, [busy, dragPayload, eventUuid, folderId, images, loadData, toastError, toastSuccess]);

  const startImageDrag = useCallback((event, image) => {
    if (!canUpload || !image?.id) return;
    const imageIds = selectedIds.has(image.id) ? Array.from(selectedIds) : [image.id];
    setDragPayload({ type: 'images', imageIds });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', imageIds.join(','));
    const preview = createDragPreview({
      count: imageIds.length,
      label: image.file_name || 'イベントアルバム画像',
      className: imageIds.length > 1 ? 'is-batch' : '',
    });
    if (preview) event.dataTransfer.setDragImage(preview, 18, 18);
  }, [canUpload, selectedIds]);

  const createFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await createEventImageFolder(eventUuid, { name, parent_folder_id: folderId });
      setNewFolderName('');
      setCreatingFolder(false);
      await loadData(folderId);
      toastSuccess('フォルダを作成しました');
    } catch (error) {
      toastError(error?.message || 'フォルダの作成に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [busy, eventUuid, folderId, loadData, newFolderName, toastError, toastSuccess]);

  const handleCustomLogo = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const compatibleFile = await normalizeImageForUpload(file);
      const reader = new FileReader();
      reader.onload = () => {
        setSettings((current) => ({
          ...current,
          logoEnabled: true,
          logoPreset: 'custom',
          customLogoDataUrl: String(reader.result || ''),
        }));
      };
      reader.readAsDataURL(compatibleFile);
    } catch (error) {
      toastError(error?.message || 'ロゴ画像を送信できる形式へ変換できませんでした');
    }
  }, [toastError]);

  const handleSelectedFiles = useCallback(async (fileList, { nextStep = 'confirm' } = {}) => {
    if (Array.from(fileList || []).length > MAX_BATCH_FILES) {
      toastError(`一度に追加できるイベントアルバム画像は${MAX_BATCH_FILES}枚までです`);
      return false;
    }
    let normalizedFiles;
    try {
      normalizedFiles = await normalizeImagesForUpload(fileList);
    } catch (error) {
      toastError(error?.message || '画像を送信できる形式へ変換できませんでした');
      return false;
    }
    const candidates = normalizedFiles.filter((file) => ACCEPTED_IMAGE_TYPES.has(file.type));
    if (!candidates.length) {
      toastError('JPEG、PNG、WebP の画像を選択してください');
      return false;
    }
    if (candidates.length > MAX_BATCH_FILES) {
      toastError(`一度に追加できるイベントアルバム画像は${MAX_BATCH_FILES}枚までです`);
      return false;
    }
    setPendingFiles(candidates);
    setUploadProgress(null);
    uploadSessionRef.current = null;
    setUploadStep(nextStep);
    return true;
  }, [toastError]);

  const handleExternalFilesDrop = useCallback(async (fileList, targetFolderId = folderId) => {
    if (!canUpload) return;
    setUploadTargetFolderId(targetFolderId ?? null);
    setUploadStep('watermark');
    setUploadOpen(true);
    if (!await handleSelectedFiles(fileList, { nextStep: 'watermark' })) setUploadOpen(false);
  }, [canUpload, folderId, handleSelectedFiles]);

  const handleGalleryDragOver = useCallback((event, targetId = 'root', springFolderId = undefined) => {
    if (!canUpload || uploadOpen || galleryOpen || !isDroppableTransfer(event.dataTransfer, dragPayload)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = dragPayload ? 'move' : 'copy';
    setDragOver(targetId === 'root');
    setDropTargetId(targetId);
    if (springFolderId === undefined) {
      clearSpringFolder();
    } else {
      scheduleSpringFolder(springFolderId, targetId);
    }
  }, [canUpload, clearSpringFolder, dragPayload, galleryOpen, scheduleSpringFolder, uploadOpen]);

  const handleGalleryDrop = useCallback((event, targetFolderId = folderId) => {
    if (!canUpload || uploadOpen || galleryOpen) return;
    if (!isDroppableTransfer(event.dataTransfer, dragPayload)) return;
    event.preventDefault();
    event.stopPropagation();
    clearSpringFolder();
    setDragOver(false);
    setDropTargetId(null);
    if (event.dataTransfer?.files?.length) {
      void handleExternalFilesDrop(Array.from(event.dataTransfer.files), targetFolderId);
      return;
    }
    moveDraggedImages(targetFolderId ?? null);
  }, [canUpload, clearSpringFolder, dragPayload, folderId, galleryOpen, handleExternalFilesDrop, moveDraggedImages, uploadOpen]);

  const uploadPendingFiles = useCallback(async () => {
    const candidates = pendingFiles;
    if (!candidates.length) {
      setUploadStep('select');
      return;
    }
    const targetFolderId = uploadTargetFolderId ?? folderId ?? null;
    const session = uploadSessionRef.current || {
      id: `event-images:${eventUuid}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      nextIndex: 0,
      pendingBatch: null,
    };
    uploadSessionRef.current = session;
    setBusy(true);
    setUploadProgress({ phase: 'processing', processed: session.nextIndex, registered: session.nextIndex, total: candidates.length, error: '' });
    try {
      while (session.nextIndex < candidates.length || session.pendingBatch) {
        if (!session.pendingBatch) {
          const startIndex = session.nextIndex;
          const chunk = candidates.slice(startIndex, startIndex + REGISTRATION_BATCH_FILES);
          const uploads = [];
          for (let chunkIndex = 0; chunkIndex < chunk.length; chunkIndex += 1) {
            const index = startIndex + chunkIndex;
            const sourceFile = chunk[chunkIndex];
            setUploadProgress({ phase: 'processing', processed: index, registered: session.nextIndex, total: candidates.length, currentName: sourceFile.name, error: '' });
            const processed = sourceFile.size > MAX_IMAGE_BYTES || settings.logoEnabled || settings.textEnabled
              ? await renderProcessedImage(sourceFile, settings, { eventName, eventDate: eventDateLabel })
              : {
                  file: sourceFile,
                  ...(await getImageDimensions(sourceFile)),
                  resized: false,
                  originalSize: sourceFile.size,
                };
            if (processed.file.size > MAX_IMAGE_BYTES) {
              throw new Error(`${sourceFile.name} を20MB以下にできませんでした。`);
            }
            setUploadProgress({ phase: 'uploading', processed: index, registered: session.nextIndex, total: candidates.length, currentName: sourceFile.name, error: '' });
            const uploadedFile = await uploadEventImageFile({ eventUuid, file: processed.file });
            uploads.push({
              file_name: uploadedFile.fileName || processed.file.name,
              original_file_name: sourceFile.name,
              file_key: uploadedFile.key,
              file_size: uploadedFile.byteSize ?? processed.file.size,
              content_type: uploadedFile.contentType || processed.file.type,
              width: uploadedFile.width ?? processed.width,
              height: uploadedFile.height ?? processed.height,
              sort_order: index,
              watermark_layers: [{
                logoPreset: settings.logoPreset,
                logoEnabled: settings.logoEnabled,
                logoFillColor: settings.logoFillColor,
                customLogoFillColor: settings.logoFillColor === 'custom' ? settings.customLogoFillColor : '',
                textPreset: settings.textPreset,
                textEnabled: settings.textEnabled,
                logoPosition: settings.logoPosition,
                textPosition: settings.textPosition,
                opacity: settings.opacity,
              }],
              client_processing_summary: { original_size: processed.originalSize, resized: processed.resized },
            });
            setUploadProgress({ phase: 'uploading', processed: index + 1, registered: session.nextIndex, total: candidates.length, currentName: sourceFile.name, error: '' });
          }
          session.pendingBatch = {
            startIndex,
            uploads,
            idempotencyKey: `${session.id}:batch:${Math.floor(startIndex / REGISTRATION_BATCH_FILES)}`,
          };
        }
        const pendingBatch = session.pendingBatch;
        setUploadProgress({ phase: 'registering', processed: pendingBatch.startIndex + pendingBatch.uploads.length, registered: session.nextIndex, total: candidates.length, error: '' });
        await createEventImageBatch(eventUuid, {
          folder_id: targetFolderId,
          idempotency_key: pendingBatch.idempotencyKey,
          images: pendingBatch.uploads,
          watermark_preset_snapshot: {
            ...settings,
            customLogoDataUrl: settings.customLogoDataUrl ? '[custom-image]' : '',
          },
        });
        session.nextIndex = pendingBatch.startIndex + pendingBatch.uploads.length;
        session.pendingBatch = null;
        setUploadProgress({ phase: 'registered', processed: session.nextIndex, registered: session.nextIndex, total: candidates.length, error: '' });
      }
      setUploadOpen(false);
      resetUploadDraft();
      await loadData(folderId);
      toastSuccess('イベントアルバムへ追加しました');
    } catch (error) {
      console.error('Event image upload failed:', error);
      setUploadProgress((current) => ({ ...(current || {}), error: error?.message || 'アップロードに失敗しました' }));
      await loadData(folderId);
      toastError(error?.message || 'イベントアルバムへの追加に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [eventDateLabel, eventName, eventUuid, folderId, loadData, pendingFiles, resetUploadDraft, settings, toastError, toastSuccess, uploadTargetFolderId]);

  const downloadSelected = useCallback(() => {
    if (!selectedIds.size) return;
    Array.from(selectedIds).forEach((id, index) => {
      window.setTimeout(() => window.open(buildEventImageFileUrl(eventUuid, id, { download: true }), '_blank', 'noopener'), index * 120);
    });
  }, [eventUuid, selectedIds]);

  const deleteSelected = useCallback(async () => {
    if (!canUpload || !selectedIds.size || busy) return;
    setBusy(true);
    try {
      await deleteEventImages(eventUuid, Array.from(selectedIds));
      setDeleteOpen(false);
      setSelectedIds(new Set());
      await loadData(folderId);
      toastSuccess('選択した画像を削除しました');
    } catch (error) {
      toastError(error?.message || 'イベントアルバム画像の削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }, [busy, canUpload, eventUuid, folderId, loadData, selectedIds, toastError, toastSuccess]);

  const selectedAllInView = useMemo(
    () => previewImages.length > 0 && previewImages.every((image) => selectedIds.has(image.id)),
    [previewImages, selectedIds]
  );

  if (!canView) return null;

  return (
    <EventImageGalleryView
      activeImage={activeImage}
      activeImageIndex={activeImageIndex}
      buildEventImageFileUrl={buildEventImageFileUrl}
      buildEventImagePreviewUrl={buildEventImagePreviewUrl}
      busy={busy}
      canDownload={canDownload}
      canUpload={canUpload}
      clearSpringFolder={clearSpringFolder}
      createFolder={createFolder}
      creatingFolder={creatingFolder}
      customLogoInputRef={customLogoInputRef}
      deleteOpen={deleteOpen}
      deleteSelected={deleteSelected}
      downloadSelected={downloadSelected}
      dragOver={dragOver}
      dropTargetId={dropTargetId}
      eventDateLabel={eventDateLabel}
      eventName={eventName}
      eventUuid={eventUuid}
      fileInputRef={fileInputRef}
      folderId={folderId}
      folders={folders}
      galleryOpen={galleryOpen}
      handleCustomLogo={handleCustomLogo}
      handleGalleryDragOver={handleGalleryDragOver}
      handleGalleryDrop={handleGalleryDrop}
      handleSelectedFiles={handleSelectedFiles}
      hasExternalFiles={hasExternalFiles}
      images={images}
      limits={limits}
      loading={loading}
      moveActiveImage={moveActiveImage}
      newFolderName={newFolderName}
      openFolder={openFolder}
      openImagePreview={openImagePreview}
      openUploadWizard={openUploadWizard}
      path={path}
      pendingFiles={pendingFiles}
      previewBusy={previewBusy}
      previewImages={previewImages}
      processedPreviews={processedPreviews}
      resetUploadDraft={resetUploadDraft}
      sampleOrientation={sampleOrientation}
      sampleTone={sampleTone}
      selectedAllInView={selectedAllInView}
      selectedCount={selectedCount}
      selectedIds={selectedIds}
      showTitle={showTitle}
      setActiveImageId={setActiveImageId}
      setCreatingFolder={setCreatingFolder}
      setDeleteOpen={setDeleteOpen}
      setDragOver={setDragOver}
      setDragPayload={setDragPayload}
      setDropTargetId={setDropTargetId}
      setGalleryOpen={setGalleryOpen}
      setNewFolderName={setNewFolderName}
      setSampleOrientation={setSampleOrientation}
      setSampleTone={setSampleTone}
      setSelectedIds={setSelectedIds}
      setSettings={setSettings}
      setUploadOpen={setUploadOpen}
      setUploadStep={setUploadStep}
      setUploadTargetFolderId={setUploadTargetFolderId}
      settings={settings}
      startImageDrag={startImageDrag}
      toggleSelected={toggleSelected}
      uploadOpen={uploadOpen}
      uploadProgress={uploadProgress}
      uploadPendingFiles={uploadPendingFiles}
      uploadStep={uploadStep}
      uploadStepIndex={uploadStepIndex}
      usage={usage}
    />
  );
}

export default EventImageGallery;
